/**
 * Compras a meses: MSI (sin intereses) y MCI (con intereses).
 *
 * En México casi todo lo caro se compra a meses, y el banco no cobra la compra
 * completa: cobra **una mensualidad por ciclo de corte**, empezando por el
 * ciclo en el que cayó la compra. Una compra de $12,000 a 12 MSI hecha el 20 de
 * agosto en una tarjeta que corta el día 5 aparece así:
 *
 *   corte 5 sep → mensualidad 1 de 12 → vence ~2 oct
 *   corte 5 oct → mensualidad 2 de 12 → vence ~2 nov
 *   …
 *
 * Y aquí está lo importante: **no hace falta ninguna regla nueva** para que la
 * mensualidad «no afecte al mes que ya pagó sino al siguiente». Cada
 * mensualidad es un cargo de su propio ciclo, y la regla que ya gobierna toda
 * la app —un cargo a tarjeta baja el disponible cuando vence el estado de
 * cuenta que lo contiene, no el día que se hace— la coloca sola en el mes que
 * le toca. Por eso todo lo de aquí se apoya en `resolveStatement` y
 * `computeDueDate` en vez de inventar su propio calendario.
 *
 * Las funciones son puras: el cliente las corre para pintar el plan al
 * instante y el servidor las corre como autoridad al calcular el disponible.
 */

import { type HolidaySet, NO_HOLIDAYS } from '../date/businessDays.js'
import {
  type PlainDate,
  addMonths,
  getMonth,
  getYear,
  isPlainDate,
  plainDate,
} from '../date/plainDate.js'
import {
  type CardCycleConfig,
  computeDueDate,
  cutoffInMonth,
  resolveStatement,
} from '../card/statementCycle.js'

/**
 * Lo mínimo que define un plan para poder calcularlo.
 *
 * Es un subconjunto de la fila `InstallmentPlan` de la base: aquí no viven ni
 * el identificador ni el estado, porque quien consulta ya filtró los planes
 * cancelados antes de llegar a estas funciones.
 */
export interface InstallmentPlan {
  /** Precio de la compra. Lo que aparece en el ticket. */
  totalAmountCents: number
  /** Plazo en meses: 3, 6, 12, 18, 24… */
  months: number
  /**
   * Mensualidad tal como la cobra el banco.
   *
   * En MSI se ignora y se recalcula: el banco reparte el precio exacto y el
   * residuo va en la última. En MCI es el dato que manda, porque incluye los
   * intereses y no se puede derivar del precio.
   */
  monthlyAmountCents: number
  /** Cuántas mensualidades llevaba pagadas **al registrar el plan**. */
  monthsPaid: number
  /** Día de la compra. De él sale el primer ciclo de corte. */
  purchasedOn: PlainDate
  /**
   * Día en que el plan entró en los libros de la app: **el ancla de dónde
   * empieza nuestra contabilidad**.
   *
   * El ciclo que estaba abierto ese día es el primero del que la app se hace
   * responsable. Todo corte anterior ya cerró —y en la mayoría de los casos ya
   * se pagó— antes de que esta compra existiera aquí, así que ninguna
   * mensualidad puede caer en él: hacerlo reescribiría meses que ya cuadraron
   * con el papel del banco.
   *
   * Es el gemelo de `stoppedOn` por el otro extremo: uno dice desde cuándo
   * cobramos, el otro hasta cuándo.
   */
  bookedOn: PlainDate
  /** MSI = `true`, MCI = `false`. */
  interestFree: boolean
}

/** Una mensualidad ubicada en su ciclo. */
export interface Installment {
  /** 1 … `months`. */
  number: number
  amountCents: number
  /** Corte del estado de cuenta que la incluye. */
  cutoffDate: PlainDate
  /** Fecha límite de ese estado de cuenta: cuando de verdad sale el dinero. */
  dueDate: PlainDate
}

function assertPlan(plan: InstallmentPlan): void {
  // Sin ancla no hay forma de saber desde qué corte responde la app, y el
  // motor emitiría cargos en cortes cerrados. Se comprueba en ejecución y no
  // solo con tipos porque el plan llega de una fila de la base: una columna
  // que se olvide de seleccionar llega como `undefined` y TypeScript no lo ve.
  if (!isPlainDate(plan.bookedOn)) {
    throw new RangeError(
      `El plan necesita el día en que entró en los libros: ${String(plan.bookedOn)}`,
    )
  }
  if (!Number.isInteger(plan.months) || plan.months < 1) {
    throw new RangeError(`El plazo debe ser un número entero de meses: ${plan.months}`)
  }
  if (!Number.isInteger(plan.monthsPaid) || plan.monthsPaid < 0) {
    throw new RangeError(`Las mensualidades pagadas no pueden ser negativas: ${plan.monthsPaid}`)
  }
  if (plan.monthsPaid > plan.months) {
    throw new RangeError(
      `No se pueden llevar ${plan.monthsPaid} mensualidades pagadas de un plazo de ${plan.months}`,
    )
  }
}

/**
 * Mensualidad base: `floor(total / meses)`.
 *
 * Se trunca hacia abajo a propósito. Redondear al centavo más cercano en cada
 * mensualidad haría que la suma se pasara del precio, y el cliente vería que
 * debe más de lo que compró.
 */
export function monthlyInstallment(totalCents: number, months: number): number {
  if (!Number.isInteger(months) || months < 1) {
    throw new RangeError(`El plazo debe ser un número entero de meses: ${months}`)
  }
  return Math.floor(totalCents / months)
}

/**
 * Los `months` montos del plan, en orden.
 *
 * **Sin override (MSI): el residuo va en la ÚLTIMA mensualidad.** Es lo que
 * hacen los bancos mexicanos y es lo que hace que la suma dé exactamente el
 * precio: $10,000 a 3 meses son 3,333.33 + 3,333.33 + 3,333.34. Repartir el
 * residuo entre las primeras (como `splitEvenly`) daría la misma suma pero
 * dejaría al cliente esperando una mensualidad distinta a la que le cobran.
 *
 * **Con override (MCI): todas iguales.** La mensualidad la fija el banco con
 * sus intereses ya dentro, así que la suma es mayor que el precio a propósito;
 * esa diferencia es el interés y se consulta con `interestCents`.
 */
export function installmentAmounts(
  totalCents: number,
  months: number,
  overrideMonthlyCents?: number,
): number[] {
  if (!Number.isInteger(months) || months < 1) {
    throw new RangeError(`El plazo debe ser un número entero de meses: ${months}`)
  }
  if (overrideMonthlyCents !== undefined) {
    if (!Number.isInteger(overrideMonthlyCents)) {
      throw new RangeError(`La mensualidad debe ir en centavos enteros: ${overrideMonthlyCents}`)
    }
    return Array.from({ length: months }, () => overrideMonthlyCents)
  }

  const base = monthlyInstallment(totalCents, months)
  const amounts = Array.from({ length: months }, () => base)
  // El residuo entero va completo a la última: `base * months` nunca pasa del
  // total porque `base` está truncado hacia abajo.
  amounts[months - 1] = base + (totalCents - base * months)
  return amounts
}

/** Los montos de un plan concreto, resolviendo solo MSI o MCI. */
export function planInstallmentAmounts(plan: InstallmentPlan): number[] {
  assertPlan(plan)
  return installmentAmounts(
    plan.totalAmountCents,
    plan.months,
    plan.interestFree ? undefined : plan.monthlyAmountCents,
  )
}

/**
 * El plan no le va a cobrar nada a esta app: se registró liquidado, o entró en
 * los libros tan tarde que todas sus mensualidades cayeron en cortes de los que
 * la app nunca se hizo responsable.
 */
export function isPlanSettled(
  plan: InstallmentPlan,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): boolean {
  return firstChargeableNumber(plan, config, holidays) > plan.months
}

/**
 * Corte del primer ciclo del plan: el del estado de cuenta donde cayó la
 * compra. Aquí se cobra la mensualidad 1, no en el siguiente.
 */
export function firstCutoffDate(
  plan: InstallmentPlan,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate {
  assertPlan(plan)
  return resolveStatement(plan.purchasedOn, config, holidays).cutoffDate
}

/**
 * Corte del primer ciclo del que la app se hace responsable: el que estaba
 * abierto el día en que el plan entró en los libros (`bookedOn`).
 *
 * Se deriva del día y no se guarda como fecha de corte a propósito, igual que
 * `stoppedOn`: la app deja cambiar el día de corte de una tarjeta, y un corte
 * guardado se quedaría apuntando a un día que ya no corta.
 */
export function accountingStartCutoff(
  plan: InstallmentPlan,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate {
  assertPlan(plan)
  return resolveStatement(plan.bookedOn, config, holidays).cutoffDate
}

/**
 * Número de la primera mensualidad que este plan puede cobrar. **Es el único
 * piso del motor**: todas las funciones de aquí lo consultan en vez de mirar
 * `monthsPaid` por su cuenta, para que no haya dos criterios sobre qué mes es
 * cobrable.
 *
 * El piso lo forman dos cosas y se toma la más restrictiva:
 *
 * - **El ancla manda siempre.** Ninguna mensualidad puede caer en un corte
 *   anterior a `accountingStartCutoff`, venga como venga `monthsPaid`.
 *   Registrar hoy (agosto) una compra de mayo con `monthsPaid = 0` no puede
 *   generar cargos en los cortes de junio, julio y agosto: esos ya cerraron y
 *   ya se pagaron, y cobrarlos ahora reescribiría historia contable. Si el
 *   número capturado y el ancla se contradicen, gana el ancla.
 * - **`monthsPaid` solo puede recortar más.** Sirve para lo que el ancla no
 *   sabe: que la persona ya iba adelantada dentro de los ciclos que sí nos
 *   tocan. Nunca resucita una mensualidad anterior al ancla.
 *
 * Como el corte de la mensualidad `n` crece mes a mes con `n`, «estar antes del
 * ancla» es exactamente «tener número menor o igual a los meses que van del
 * primer corte al ancla»: por eso el piso se puede expresar como un número.
 */
export function firstChargeableNumber(
  plan: InstallmentPlan,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): number {
  const first = firstCutoffDate(plan, config, holidays)
  const anchor = accountingStartCutoff(plan, config, holidays)
  const monthsBefore = (getYear(anchor) - getYear(first)) * 12 + (getMonth(anchor) - getMonth(first))
  // Se acota al plazo: un ancla posterior a la última mensualidad deja el plan
  // sin nada que cobrar, no con un piso fuera de rango.
  const skipped = Math.min(plan.months, Math.max(0, monthsBefore))
  return Math.max(plan.monthsPaid, skipped) + 1
}

/**
 * Corte de la mensualidad `number`.
 *
 * Se cuenta desde el mes del primer corte y se vuelve a recortar cada mes, en
 * vez de ir sumando un mes a la fecha anterior. Con corte el 31 la diferencia
 * es real: encadenando quedaría 31 ene → 28 feb → 28 mar, y la tarjeta corta el
 * 31 de marzo.
 */
function cutoffOfNumber(
  firstCutoff: PlainDate,
  number: number,
  statementDay: number,
): PlainDate {
  const anchor = addMonths(plainDate(getYear(firstCutoff), getMonth(firstCutoff), 1), number - 1)
  return cutoffInMonth(getYear(anchor), getMonth(anchor), statementDay)
}

function buildInstallment(
  number: number,
  amountCents: number,
  cutoffDate: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet,
): Installment {
  return {
    number,
    amountCents,
    cutoffDate,
    dueDate: computeDueDate(cutoffDate, config, holidays),
  }
}

/**
 * Calendario completo del plan, de la mensualidad 1 a la última.
 *
 * Incluye las que ya se habían pagado antes de registrarlo: sirve para pintar
 * el historial. Para lo que falta, `remainingInstallments`.
 */
export function installmentSchedule(
  plan: InstallmentPlan,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): Installment[] {
  const amounts = planInstallmentAmounts(plan)
  const first = firstCutoffDate(plan, config, holidays)

  return amounts.map((amountCents, index) =>
    buildInstallment(
      index + 1,
      amountCents,
      cutoffOfNumber(first, index + 1, config.statementDay),
      config,
      holidays,
    ),
  )
}

/**
 * Qué mensualidad cae en el ciclo que cortó en `cutoffDate`, o `null`.
 *
 * Devuelve `null` en los cuatro casos en que el ciclo no lleva cargo: el plan
 * todavía no empezaba, ya terminó, esa mensualidad ya venía pagada al
 * registrarlo, o el corte es anterior al ancla contable del plan.
 *
 * **Ese último caso es el que impide reescribir cortes cerrados.** No depende
 * de que `monthsPaid` venga bien capturado: aunque diga cero, un corte anterior
 * a `accountingStartCutoff` nunca recibe cargo.
 */
export function installmentForCycle(
  plan: InstallmentPlan,
  cutoffDate: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): Installment | null {
  assertPlan(plan)

  // Si la fecha no es un corte real de esta tarjeta no hay nada que ubicar:
  // pedir la mensualidad de un día cualquiera es un error de quien llama.
  const expected = cutoffInMonth(getYear(cutoffDate), getMonth(cutoffDate), config.statementDay)
  if (expected !== cutoffDate) return null

  const first = firstCutoffDate(plan, config, holidays)
  const monthsFromFirst =
    (getYear(cutoffDate) - getYear(first)) * 12 + (getMonth(cutoffDate) - getMonth(first))
  const number = monthsFromFirst + 1

  if (number < 1 || number > plan.months) return null
  if (number < firstChargeableNumber(plan, config, holidays)) return null

  const amounts = planInstallmentAmounts(plan)
  return buildInstallment(number, amounts[number - 1] as number, cutoffDate, config, holidays)
}

/**
 * Las mensualidades que faltan por pagar en `today`.
 *
 * «Falta» significa dos cosas a la vez: que el plan la pueda cobrar
 * (`firstChargeableNumber`, que junta el ancla contable y `monthsPaid`) y que
 * su estado de cuenta todavía no vence. La que ya cortó pero aún no vence sigue
 * contando: el dinero no ha salido.
 */
export function remainingInstallments(
  plan: InstallmentPlan,
  today: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): Installment[] {
  const from = firstChargeableNumber(plan, config, holidays)
  return installmentSchedule(plan, config, holidays).filter(
    (installment) => installment.number >= from && installment.dueDate >= today,
  )
}

/**
 * Las mensualidades cuyo estado de cuenta vence dentro de `[from, to]`.
 *
 * Es lo que compromete dinero en el motor del disponible, con la misma regla
 * que un estado de cuenta cualquiera.
 */
export function installmentsDueBetween(
  plan: InstallmentPlan,
  from: PlainDate,
  to: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): Installment[] {
  const fromNumber = firstChargeableNumber(plan, config, holidays)
  return installmentSchedule(plan, config, holidays).filter(
    (installment) =>
      installment.number >= fromNumber &&
      installment.dueDate >= from &&
      installment.dueDate <= to,
  )
}

/**
 * Lo que el plan debía al entrar en los libros: la suma de todas las
 * mensualidades que la app se compromete a cobrar, de la primera cobrable a la
 * última. No depende de la fecha de hoy, así que es estable como dato del plan;
 * para el saldo de hoy está `remainingBalanceCents`.
 *
 * Pide la configuración de la tarjeta porque el piso lo fija el ancla contable,
 * y el ancla se ubica en el calendario de cortes de esa tarjeta. Derivarlo solo
 * de `monthsPaid` daría un número mayor que `remainingBalanceCents` en el mismo
 * plan —un total que nunca se va a cobrar— y la pantalla mostraría dos deudas
 * distintas para la misma compra.
 */
export function outstandingBalanceCents(
  plan: InstallmentPlan,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): number {
  const amounts = planInstallmentAmounts(plan)
  const from = firstChargeableNumber(plan, config, holidays)
  let total = 0
  for (let i = from - 1; i < amounts.length; i++) total += amounts[i] as number
  return total
}

/** Suma de lo que falta por pagar en `today`. */
export function remainingBalanceCents(
  plan: InstallmentPlan,
  today: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): number {
  let total = 0
  for (const installment of remainingInstallments(plan, today, config, holidays)) {
    total += installment.amountCents
  }
  return total
}

/** Todo lo que costará el plan: el precio en MSI, precio más intereses en MCI. */
export function totalToPayCents(plan: InstallmentPlan): number {
  let total = 0
  for (const amount of planInstallmentAmounts(plan)) total += amount
  return total
}

/**
 * Interés del plan: cero en MSI, y en MCI lo que se paga de más sobre el
 * precio. Se acota a cero porque una mensualidad capturada por debajo de
 * `total / meses` es un dato mal escrito, no un descuento del banco.
 */
export function interestCents(plan: InstallmentPlan): number {
  assertPlan(plan)
  if (plan.interestFree) return 0
  return Math.max(0, plan.months * plan.monthlyAmountCents - plan.totalAmountCents)
}

/** Avance del plan en `today`: lo que la pantalla necesita de un vistazo. */
export interface InstallmentProgress {
  paidCount: number
  remainingCount: number
  paidCents: number
  remainingCents: number
  /** La siguiente por pagar, o `null` si ya no queda ninguna. */
  next: Installment | null
  /** La última del plazo. `null` solo si el plan es inválido de plazo cero. */
  last: Installment | null
}

export function installmentProgress(
  plan: InstallmentPlan,
  today: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): InstallmentProgress {
  const schedule = installmentSchedule(plan, config, holidays)
  const from = firstChargeableNumber(plan, config, holidays)
  const remaining = schedule.filter(
    (installment) => installment.number >= from && installment.dueDate >= today,
  )

  let remainingCents = 0
  for (const installment of remaining) remainingCents += installment.amountCents

  let scheduleCents = 0
  for (const installment of schedule) scheduleCents += installment.amountCents

  return {
    paidCents: scheduleCents - remainingCents,
    paidCount: schedule.length - remaining.length,
    remainingCount: remaining.length,
    remainingCents,
    next: remaining[0] ?? null,
    last: schedule[schedule.length - 1] ?? null,
  }
}

/**
 * Cuántas mensualidades ha cobrado ya la tarjeta: las que caen en un corte que
 * ya ocurrió.
 *
 * **Este número no se le debe preguntar a nadie.** Con la fecha de la compra y
 * el día de corte de la tarjeta se sabe exactamente cuántos estados de cuenta
 * han pasado, y por tanto cuántas mensualidades cobró el banco. Pedirlo a mano
 * era pedirle a quien está aprendiendo justo la cuenta que la app puede hacer
 * sola —y equivocarse por uno deja el calendario entero corrido un mes, con la
 * siguiente mensualidad anunciada en el corte que no es—.
 *
 * Es una **sugerencia**, no una imposición: quien pagó por adelantado lleva más
 * de las que se cobraron, y quien viene atrasado no ha pagado todas las que le
 * facturaron. Por eso el formulario ofrece esta cifra y deja corregirla, en vez
 * de calcularla en silencio.
 *
 * No lleva `monthsPaid` dentro a propósito: es la cifra contra la que se compara
 * lo que la persona declaró, así que tomarla de ahí sería morderse la cola.
 */
export function billedInstallmentsCount(
  input: { purchasedOn: PlainDate; months: number },
  today: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): number {
  if (!isPlainDate(input.purchasedOn)) return 0
  if (!Number.isInteger(input.months) || input.months < 1) return 0

  const first = resolveStatement(input.purchasedOn, config, holidays).cutoffDate
  let cobradas = 0
  for (let numero = 1; numero <= input.months; numero++) {
    const corte = cutoffOfNumber(first, numero, config.statementDay)
    if (corte > today) break
    cobradas++
  }
  return cobradas
}
